import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { CustomRoleSelect } from './CustomRoleSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { User, Loader2, AlertCircle, MessageSquare, Plus, FileText, Check, Trash2 } from 'lucide-react';
import { ROLES } from './invitationConstants';

export default function InvitationFormTab(ctx) {
  const {
    formData, handleInputChange,
    emailCheckResult, checkingEmail,
    showNewMessageForm, setShowNewMessageForm,
    newMessageData, setNewMessageData, handleCreateMessage, savingMessage,
    messagesPredefinis, selectedMessageId, setSelectedMessageId,
    handleSelectMessage, handleDeleteMessage, isSuperAdmin,
  } = ctx;

  return (
    <TabsContent value="formulaire" className="mt-4 space-y-4">
      {/* Sélection du rôle */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Type de compte</Label>
        <div className="grid grid-cols-2 gap-3">
          {ROLES.map((role) => {
            const Icon = role.icon;
            const isSelected = formData.role === role.value;
            return (
              <div
                key={role.value}
                onClick={() => handleInputChange('role', role.value)}
                className={`
                  p-4 rounded-lg border-2 cursor-pointer transition-all
                  ${isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-semibold">{role.label}</p>
                    <p className="text-xs text-gray-500">{role.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {isSuperAdmin && (
        <CustomRoleSelect value={formData.custom_role} onChange={(v) => handleInputChange('custom_role', v)} />
      )}

      {/* Informations personnelles */}
      <div className="bg-slate-50 rounded-lg p-4 space-y-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <User className="h-4 w-4 text-blue-600" />
          Informations personnelles
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Prénom <span className="text-red-500">*</span></Label>
            <Input
              value={formData.prenom}
              onChange={(e) => handleInputChange('prenom', e.target.value)}
              placeholder="Jean"
              className="mt-1.5 bg-white"
            />
          </div>
          <div>
            <Label>Nom <span className="text-red-500">*</span></Label>
            <Input
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              placeholder="DUPONT"
              className="mt-1.5 bg-white"
            />
          </div>
          <div>
            <Label>Email <span className="text-red-500">*</span></Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="jean.dupont@email.fr"
              className={`mt-1.5 bg-white ${emailCheckResult?.exists ? 'border-amber-400' : ''}`}
            />
            {checkingEmail && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Vérification...
              </p>
            )}
            {emailCheckResult?.exists && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm w-full">
                    <p className="font-medium text-blue-800">
                      Utilisateur existant
                    </p>
                    <p className="text-blue-700 mt-1">
                      <strong>{emailCheckResult.user?.prenom} {emailCheckResult.user?.nom}</strong>
                    </p>

                    {/* Rôles actuels */}
                    {emailCheckResult.current_roles && emailCheckResult.current_roles.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-blue-600 mb-1">Rôles actuels:</p>
                        <div className="flex flex-wrap gap-1">
                          {emailCheckResult.current_roles.map((r, idx) => (
                            <span key={idx} className={`px-2 py-0.5 rounded text-xs ${
                              r.role === emailCheckResult.user?.primary_role
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {r.label} {r.role === emailCheckResult.user?.primary_role && '(principal)'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Message selon si le rôle est nouveau ou existant */}
                    {emailCheckResult.current_roles?.some(r => r.role === formData.role ||
                      (r.role === 'member' && formData.role === 'membre') ||
                      (r.role === 'membre' && formData.role === 'member')) ? (
                      <p className="text-amber-700 text-xs mt-2 bg-amber-50 p-2 rounded">
                        ⚠️ L&apos;utilisateur a déjà ce rôle. Une réinvitation générera un nouveau mot de passe.
                      </p>
                    ) : (
                      <p className="text-green-700 text-xs mt-2 bg-green-50 p-2 rounded font-medium">
                        ✓ Le rôle <strong>{ROLES.find(r => r.value === formData.role)?.label}</strong> sera ajouté à cet utilisateur avec un nouveau profil.
                      </p>
                    )}

                    {/* Rôles disponibles */}
                    {emailCheckResult.available_roles && emailCheckResult.available_roles.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="text-xs text-blue-600">Rôles encore disponibles: {emailCheckResult.available_roles.map(r => r.label).join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input
              value={formData.telephone}
              onChange={(e) => handleInputChange('telephone', e.target.value)}
              placeholder="+33 6 12 34 56 78"
              className="mt-1.5 bg-white"
            />
          </div>
          {(formData.role === 'cooper' || formData.role === 'expert') && (
            <div className="col-span-2">
              <Label>Entreprise / Cabinet</Label>
              <Input
                value={formData.entreprise}
                onChange={(e) => handleInputChange('entreprise', e.target.value)}
                placeholder="Nom de l'entreprise"
                className="mt-1.5 bg-white"
              />
            </div>
          )}
        </div>
      </div>

      {/* Message personnalisé avec messages prédéfinis */}
      <div className="bg-slate-50 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-600" />
            Message personnalisé
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowNewMessageForm(!showNewMessageForm)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nouveau modèle
          </Button>
        </div>

        {/* Formulaire nouveau message */}
        {showNewMessageForm && (
          <div className="bg-white border border-blue-200 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              Créer un modèle de message
            </h4>
            <Input
              placeholder="Titre du modèle"
              value={newMessageData.titre}
              onChange={(e) => setNewMessageData(prev => ({ ...prev, titre: e.target.value }))}
              className="text-sm"
            />
            <Textarea
              placeholder="Contenu du message..."
              value={newMessageData.contenu}
              onChange={(e) => setNewMessageData(prev => ({ ...prev, contenu: e.target.value }))}
              className="min-h-[60px] text-sm"
            />
            <Select
              value={newMessageData.role_cible || 'all'}
              onValueChange={(v) => setNewMessageData(prev => ({ ...prev, role_cible: v === 'all' ? '' : v }))}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Pour quel rôle ?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="cooper">COOPER&apos;S uniquement</SelectItem>
                <SelectItem value="expert">Experts uniquement</SelectItem>
                <SelectItem value="membre">Membres uniquement</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowNewMessageForm(false)}
              >
                Annuler
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleCreateMessage}
                disabled={savingMessage}
              >
                {savingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        )}

        {/* Liste des messages prédéfinis */}
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">Messages prédéfinis</Label>
          <div className="grid grid-cols-1 gap-2 max-h-[150px] overflow-y-auto">
            {messagesPredefinis
              .filter(m => !m.role_cible || m.role_cible === formData.role)
              .map((message) => (
                <div
                  key={message.id}
                  onClick={() => handleSelectMessage(message.id)}
                  className={`
                    p-3 rounded-lg border cursor-pointer transition-all flex items-start justify-between gap-2
                    ${selectedMessageId === message.id
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className={`h-4 w-4 flex-shrink-0 ${selectedMessageId === message.id ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm truncate">{message.titre}</span>
                      {message.role_cible && (
                        <Badge variant="outline" className="text-xs">
                          {message.role_cible === 'cooper' ? "COOPER'S" : message.role_cible}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{message.contenu}</p>
                  </div>
                  {!message.id.startsWith('default-') && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 h-6 w-6 p-0 text-red-400 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMessage(message.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Zone de texte pour message personnalisé */}
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">Ou écrivez votre propre message</Label>
          <Textarea
            value={formData.message_personnalise}
            onChange={(e) => {
              handleInputChange('message_personnalise', e.target.value);
              setSelectedMessageId(null);
            }}
            placeholder="Ajoutez un message personnalisé qui sera inclus dans l'email d'invitation..."
            className="bg-white min-h-[80px]"
          />
        </div>
      </div>

      {/* Options d'envoi */}
      <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
        <div>
          <Label className="font-medium">Envoyer l&apos;email automatiquement</Label>
          <p className="text-sm text-gray-500">L&apos;utilisateur recevra ses identifiants par email</p>
        </div>
        <Switch
          checked={formData.envoyer_email}
          onCheckedChange={(checked) => handleInputChange('envoyer_email', checked)}
        />
      </div>
    </TabsContent>
  );
}
